export default class Dropdown extends HTMLElement {
    static CLASS_HTML = 'has-dropdown-open';
    static CLASS_ACTIVE = 'is-active';
    static CLASS_CURRENT = 'is-current';
    static EASING = 'cubic-bezier(0.40, 0.00, 0.00, 1.00)';
    static DURATION = 500;

    constructor(m) {
        super(m);

        // UI
        this.$toggler = this.querySelector('[data-dropdown-toggler]');
        this.$container = this.querySelector('[data-dropdown-container]');
        this.$menu = this.querySelector('[data-dropdown-menu]');
        this.$menuitems = this.querySelectorAll('[role="menuitem"]');
        this.$firstMenuitem = null;
        this.$lastMenuitem = null;
        const $parent = this.closest('[data-dropdown-parent]');
        this.$siblings = $parent
            ? Array.from($parent.querySelectorAll('c-dropdown')).filter((el) => el !== this)
            : [];

        // Binding
        this.onTogglerKeydown = this.onTogglerKeydown.bind(this);
        this.onTogglerClick = this.onTogglerClick.bind(this);
        this.onMenuitemKeydown = this.onMenuitemKeydown.bind(this);
        this.onMenuitemMouseover = this.onMenuitemMouseover.bind(this);
        this.onClickOutside = this.onClickOutside.bind(this);
    }

    // =============================================================================
    // Lifecycle methods
    // =============================================================================

    connectedCallback() {
        this.bindEvents();

        this.$menuitems.forEach(($menuitem) => {
            $menuitem.tabIndex = -1;

            if (!this.$firstMenuitem) {
                this.$firstMenuitem = $menuitem;
            }
            this.$lastMenuitem = $menuitem;
        });
    }

    disconnectedCallback() {
        this.unbindEvents();
        document.documentElement.classList.remove(Dropdown.CLASS_HTML);
    }

    // =============================================================================
    // Events
    // =============================================================================
    bindEvents() {
        this.$toggler.addEventListener('keydown', this.onTogglerKeydown);
        this.$toggler.addEventListener('click', this.onTogglerClick);

        window.addEventListener('mousedown', this.onClickOutside, true);

        this.$menuitems.forEach(($menuitem) => {
            $menuitem.addEventListener('keydown', this.onMenuitemKeydown);
            $menuitem.addEventListener('mouseover', this.onMenuitemMouseover);
        });
    }

    unbindEvents() {
        this.$toggler.removeEventListener('keydown', this.onTogglerKeydown);
        this.$toggler.removeEventListener('click', this.onTogglerClick);

        window.removeEventListener('mousedown', this.onClickOutside, true);

        this.$menuitems.forEach(($menuitem) => {
            $menuitem.removeEventListener('keydown', this.onMenuitemKeydown);
            $menuitem.removeEventListener('mouseover', this.onMenuitemMouseover);
        });
    }

    // =============================================================================
    // Callbacks
    // =============================================================================

    onTogglerClick(event) {
        if (this.isOpen()) {
            this.close();
            this.$toggler.focus();
        } else {
            this.open(this.$firstMenuitem);
        }

        event.stopPropagation();
        event.preventDefault();
    }

    onTogglerKeydown(event) {
        var key = event.key,
            flag = false;

        switch (key) {
            case ' ':
            case 'Enter':
            case 'ArrowDown':
            case 'Down':
                this.open(this.$firstMenuitem);
                flag = true;
                break;

            case 'Esc':
            case 'Escape':
                this.close();
                this.$toggler.focus();
                flag = true;
                break;

            case 'Up':
            case 'ArrowUp':
                this.open(this.$lastMenuitem);
                flag = true;
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    onMenuitemKeydown(event) {
        var $target = event.currentTarget,
            key = event.key,
            flag = false;

        function isPrintableCharacter(str) {
            return str.length === 1 && str.match(/\S/);
        }

        if (event.ctrlKey || event.altKey || event.metaKey) {
            return;
        }

        if (event.shiftKey) {
            if (isPrintableCharacter(key)) {
                this.setFocusByFirstCharacter($target, key);
                flag = true;
            }

            if (event.key === 'Tab') {
                this.$toggler.focus();
                this.close();
                flag = true;
            }
        } else {
            switch (key) {
                case ' ':
                    window.location.href = $target.href;
                    break;

                case 'Esc':
                case 'Escape':
                    this.close();
                    this.$toggler.focus();
                    flag = true;
                    break;

                case 'Up':
                case 'ArrowUp':
                    this.setFocusToPreviousMenuitem($target);
                    flag = true;
                    break;

                case 'ArrowDown':
                case 'Down':
                    this.setFocusToNextMenuitem($target);
                    flag = true;
                    break;

                case 'Home':
                case 'PageUp':
                    this.setFocusToFirstMenuitem();
                    flag = true;
                    break;

                case 'End':
                case 'PageDown':
                    this.setFocusToLastMenuitem();
                    flag = true;
                    break;

                case 'Tab':
                    this.close();
                    break;

                default:
                    if (isPrintableCharacter(key)) {
                        this.setFocusByFirstCharacter($target, key);
                        flag = true;
                    }
                    break;
            }
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    onMenuitemMouseover(event) {
        const $target = event.currentTarget;
        $target && $target.focus();
    }

    onClickOutside(event) {
        if (!this.contains(event.target)) {
            if (this.isOpen()) {
                this.close();
                this.$toggler.focus();
            }
        }
    }

    onAnimationFinish(isOpen, $itemToFocus = null) {
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;

        this.$container.style.height = this.$container.style.overflow = '';

        $itemToFocus && $itemToFocus.focus();

        if (!isOpen) {
            this.$menuitems.forEach(($menuitem) => ($menuitem.tabIndex = -1));
        }
    }

    // =============================================================================
    // Methods
    // =============================================================================

    setFocusToMenuitem($newMenuitem) {
        this.$menuitems.forEach(($item) => {
            if ($item === $newMenuitem) {
                $item.tabIndex = 0;
                $newMenuitem.focus();
                requestAnimationFrame(() => $newMenuitem.focus());
            } else {
                $item.tabIndex = -1;
            }
        });
    }

    setFocusToFirstMenuitem() {
        this.setFocusToMenuitem(this.$firstMenuitem);
    }

    setFocusToLastMenuitem() {
        this.setFocusToMenuitem(this.$lastMenuitem);
    }

    setFocusToPreviousMenuitem($currentMenuitem) {
        let $newMenuitem, index;

        if ($currentMenuitem === this.$firstMenuitem) {
            $newMenuitem = this.$lastMenuitem;
        } else {
            index = Array.from(this.$menuitems).indexOf($currentMenuitem);
            $newMenuitem = this.$menuitems[index - 1];
        }

        this.setFocusToMenuitem($newMenuitem);

        return $newMenuitem;
    }

    setFocusToNextMenuitem($currentMenuitem) {
        let $newMenuitem, index;

        if ($currentMenuitem === this.$lastMenuitem) {
            $newMenuitem = this.$firstMenuitem;
        } else {
            index = Array.from(this.$menuitems).indexOf($currentMenuitem);

            $newMenuitem = this.$menuitems[index + 1];
        }

        this.setFocusToMenuitem($newMenuitem);

        return $newMenuitem;
    }

    // Popup menu methods

    open($itemToFocus) {
        this.$toggler.setAttribute('aria-expanded', 'true');
        this.$container.style.overflow = 'hidden';
        this.$container.style.height = '0px';

        window.requestAnimationFrame(() => {
            this.isExpanding = true;
            this.classList.add(Dropdown.CLASS_ACTIVE);
            this.classList.add(Dropdown.CLASS_CURRENT);
            document.documentElement.classList.add(Dropdown.CLASS_HTML);

            this.$siblings.forEach(($sibling) => {
                $sibling.classList.remove(Dropdown.CLASS_CURRENT);
            });

            const startHeight = '0px';
            const endHeight = `${this.$menu.offsetHeight}px`;

            if (this.animation) {
                this.animation.cancel();
            }

            this.animation = this.$container.animate(
                {
                    height: [startHeight, endHeight]
                },
                {
                    duration: Dropdown.DURATION,
                    easing: Dropdown.EASING
                }
            );

            this.animation.onfinish = () => this.onAnimationFinish(true, $itemToFocus);
            this.animation.oncancel = () => {
                this.isExpanding = false;
                this.classList.remove(Dropdown.CLASS_ACTIVE);
                document.documentElement.classList.remove(Dropdown.CLASS_HTML);
            };
        });
    }

    close() {
        if (!this.isOpen() || this.isClosing) return;

        this.$toggler.setAttribute('aria-expanded', 'false');
        this.classList.remove(Dropdown.CLASS_ACTIVE);
        document.documentElement.classList.remove(Dropdown.CLASS_HTML);

        this.isClosing = true;
        this.$container.style.overflow = 'hidden';

        const startHeight = `${this.$menu.offsetHeight}px`;
        const endHeight = '0px';

        if (this.animation) {
            this.animation.cancel();
        }

        this.animation = this.$container.animate(
            {
                height: [startHeight, endHeight]
            },
            {
                duration: Dropdown.DURATION,
                easing: Dropdown.EASING
            }
        );

        this.animation.onfinish = () => this.onAnimationFinish();
        this.animation.oncancel = () => {
            this.isClosing = false;
            this.classList.add(Dropdown.CLASS_ACTIVE);
            document.documentElement.classList.add(Dropdown.CLASS_HTML);
        };
    }

    isOpen() {
        return this.$toggler.getAttribute('aria-expanded') === 'true';
    }
}
